import {passwordHint} from "@/js/accounts/utils/helpers.js";
import {API} from "@/js/accounts/utils/api.js";
import {algoliaIndexName, algoliaSearchClient} from "@/js/accounts/utils/algolia.js";
import {pathways, registrationMachine} from "@/js/accounts/machines/registration-machine.js";
import {autocomplete} from "@algolia/autocomplete-js";
import '@algolia/autocomplete-theme-classic';
import variableSettings from "@/js/accounts/utils/variable-settings.js";
const environment = window.envvars?.environment || 'production';

const settings = variableSettings[(environment === 'production' ? 'PROD' : 'TEST')];
export default () => ({
    state: registrationMachine.state,
    context: {...registrationMachine.context},
    pathways,
    error: '',
    nextTo: "/account/my-account/",
    csrfToken: false,
    searchClient: false,
    autocompleteInstance: null,
    showPassword: false,
    errors: {},
    unableToFindSchool: false,
    existingAccount: false,
    email: '',
    firstname: '',
    lastname: '',
    phoneNumber: '',
    password: '',
    returnURL: '',
    numberOfUsers: 1,
    allCountries: [],
    allStates: [],
    filteredStates: [],
    manualSchoolInput: {
        schoolName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
    },
    schoolForm: {
        schoolName: {
            label: 'School Name',
            field: 'input',
            type: 'text',
            placeholder: 'e.g. My School Name',
            required: true,
            pathwaysIncluded: ["international", "independent-school"]
        },
        addressLine1: {
            label: 'Address Line 1',
            field: 'input',
            type: 'text',
            placeholder: 'e.g. 123 School Lane',
            required: true,
        },
        addressLine2: {
            label: 'Address Line 2',
            field: 'input',
            type: 'text',
            placeholder: 'e.g. Building Name or Flat Number',
            required: false,
        },
        city: {
            label: 'City',
            field: 'input',
            type: 'text',
            placeholder: 'e.g. London',
            required: true,
        },
        country: {
            label: 'Country',
            type: 'select',
            required: true,
        },
        state: {
            label: 'State/Region',
            field: 'select',
            required: true,
        },
        postalCode: {
            label: 'Zip/Postal Code',
            field: 'input',
            type: 'text',
            placeholder: 'e.g. AB12 3CD',
            required: true,
        },
    },
    subscriptionPlans: {
        'monthly': {
            label: 'Monthly Subscription',
            price: '39.99',
            period: 'monthly',
            description: 'Pay monthly for access to all Globe features.',
        },
        'annual': {
            label: 'Annual Subscription',
            period: 'annual',
            price: {
                1: 60,
                5: 250,
                10: 400,
            },
            numberOfUsersLabel: {
                1: 'Single user',
                5: ' up to 2 - 5',
                10: 'up to 6 - 10',
            },
            description: 'Save 20% with an annual subscription.',
        },
        'individual': {
            label: 'Annual plan for individuals',
            period: 'annual',
            price: '60',
            numberOfUsersLabel: {
                1: 'Single user',
            },
            description: 'Save 20%',
        }
    },
    prices: {
        '1': 60,
        '5': 250,
        '10': 400,
        '11': 400,
    },
    get displayNumberOfUsers() {
        if (Object.keys(this.selectedPlan).length === 0) {
            return ""
        }
        return this.selectedPlan?.numberOfUsersLabel[this.numberOfUsers] || ""
    },
    get displayTotalPrice() {
        if (Object.keys(this.selectedPlan).length === 0) {
            return ""
        }
        return this.selectedPlan.price[this.numberOfUsers]
    },
    get displayPrice() {
        if (!this.numberOfUsers) return 'Please select number of teachers';
        if (this.numberOfUsers === '11') {
            return `Please contact Globe to arrange this setup <a href="mailto:teach@shakespearesglobe.com">teach@shakespearesglobe.com</a>`
        }
        const price = this.prices[this.numberOfUsers] || 0;
        return `Annual plan £${price.toFixed(2)}`;
    },
    memberLevelSeatNumbers: settings.memberLevelSeatNumbers,
    selectedPlan: {},
    messages: {
        existingAccount: 'The email address you entered is already associated with an account. Please log in or try again with another email address.',
        unableToFindSchool:
            'First, check you\'ve selected the correct school type. Try searching by your school\'s full name or postcode. If it\'s still missing, get in touch at <a href="mailto:teach@shakespearesglobe.com">teach@shakespearesglobe.com</a>',
    },
    offerAdmin: false,
    loading: false,
    isReady: false,
    paymentProcessingStatus: {
        inProgress: true,
        status: "inProgress",
        messaging: {
            inProgress: 'Your payment is being processed. Please do not close this window.',
            approved: 'Your payment is approved.',
            declined: 'Your payment was declined.',
            cancelled: 'Your payment is cancelled.',
            failed: 'Your payment has failed, please try again.',
        },
    },
    paymentStatusMessage() {
        return this.paymentProcessingStatus.messaging[this.paymentProcessingStatus.status];
    },
    get selectedSchoolSummary() {
        const school = this.context.selectedSchool;
        console.log(school)
        if (!school) return {name: '', address: ''};

        const country = this.allCountries.find(c => c.value === school.country)?.label || '';
        const state = this.allStates.find(c => c.value === school.state || c.label === school.county)?.label || '';

        const parts = [
            school.addressLine1,
            school.addressLine2,
            school.addressLine3,
            school.city,
            state,
            school.postalCode,
            country,
        ].filter(Boolean);

        return {
            name: school.schoolName || school.name || '',
            address: parts.join(', '),
            type: school.type || '',
        };
    },
    async registerInit() {
        const params = new URLSearchParams(window.location.search);
        this.nextTo = params.get("next") || "/account/my-account/";
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        this.searchClient = algoliaSearchClient;

        const linkSchool = params.has("link-school");
        const paymentStatus = params.get("paymentStatus"); // keep the value, not just boolean
        const userConstituentId = parseInt(this.$root.dataset.userConstituentId);
        this.context.constituentId = (linkSchool || paymentStatus) ? userConstituentId : null;

        // Ensure Alpine ticks data updates
        this.loading = true;
        await this.$nextTick();

        // Preload data needed for the registration process
        const preload = Promise.all([this.fetchCountries(), this.fetchStates()])
            .then(([countries, states]) => {
                this.allCountries = countries;
                this.allStates = states;
                this.filteredStates = states;
            }).catch(err => {
                console.error('Preload error:', err);
                this.error = 'Failed to load location data.';
            });

        try {
            if (paymentStatus) {
                await this.handlePaymentProcess(userConstituentId);
            } else {
                await this.nonPaymentRegisterInit();
            }
        } finally {
            await preload;
            this.loading = false;
        }

    },
    async nonPaymentRegisterInit() {
        console.log('Non payment register init');
        let stepType = "START"
        const params = new URLSearchParams(window.location.search);
        if (params.has("returnURL") && params.has("email")) {
            this.context.email = params.get("email") ? decodeURIComponent(params.get("email")) : "";
            this.returnURL = params.get("returnURL") ? decodeURIComponent(params.get("returnURL")) : "";
            if (this.returnURL) {
                this.nextTo = this.returnURL;
            }
            stepType = "SIGN_UP_VIA_TOKEN"
        }
        // Normal initialization
        let result = registrationMachine.transition(this.state, stepType, {
            constituentId: this.context.constituentId,
        });
        this.state = result.nextState;

        // Fetch session data if we have a constituent
        if (this.context.constituentId) {
            await this.fetchSessionData();
        }

        this.setupWatchers();

        // Create session at the end
        API.POST.sessionCreate()
            .catch(error => {
                this.error = 'An error occurred while logging in. Please try again.';
                console.error('Login error:', error);
            });
    },
    async fetchSessionData() {
        try {
            this.loading = true;
            const response = await API.GET.sessionData(this.context.constituentId);
            if (!response.ok) throw new Error('Failed to fetch session data');
            const data = await response.json();
            this.context.email = data.session_data?.login_info?.email || '';
            this.context.sessionData = data.session_data || {};
        } catch (error) {
            console.error('Error fetching session data:', error);
            this.error = 'Failed to fetch session data. Please try again later.';
        } finally {
            this.loading = false;
        }
    },
    setPaymentStatus(status) {
        this.paymentProcessingStatus.status = status;
    },
    async handlePaymentProcess(constituentId) {
        console.log("Payment status detected, entering payment process...");
        console.log("Main loading status:", this.loading)
        console.log(constituentId)

        let result = registrationMachine.transition(this.state, 'PAYMENT_PROCESS', {
            constituentId,
        });
        this.state = result.nextState;

        try {
            await this.fetchSessionData();
            console.log(constituentId);

            const paymentStatus = new URLSearchParams(window.location.search).get("paymentStatus");
            if (['approved', 'declined', 'cancelled'].includes(paymentStatus)) {
                if (paymentStatus === 'approved') {
                    // Check the order from Tessitura to confirm order has been processed
                    const transactionId = new URLSearchParams(window.location.search).get("sessionId")
                    const ordersResponse = await API.GET.fetchOrders(transactionId)
                    if (!ordersResponse.ok) {
                        throw new Error('Failed to fetch orders from Tessitura');
                    }
                    const orders = await ordersResponse.json();
                    console.log(orders);
                    if (!orders?.details) {
                        throw new Error('No orders found for the user.');
                    }

                    // Switch Session to ensure the membership subscription takes effect
                    // await API.POST.sessionTransfer(this.csrfToken)
                    // extract membership details from the order
                    const membershipProductDetails = orders.details.Products.find(p => p.ProductClass.Description === 'Membership');
                    const constituentDetail = orders.details.Constituent
                    console.log("Membership details from order:", membershipProductDetails);
                    if (membershipProductDetails) {
                        const membershipDetails = membershipProductDetails.Membership;
                        if (membershipDetails.MembershipLevel.Id === settings.IndividualMembershipLevelId && constituentDetail.Id === membershipDetails.Recipient.Id) {
                            // Individual membership
                            this.context.pathwaySteps = this.pathways['individual']?.steps || [];
                            this.paymentProcessingStatus.inProgress = false;
                            this.setPaymentStatus(paymentStatus);
                            console.log("Individual membership processed, should got to done");
                            this.paymentComplete()
                            return
                        } else {
                            // This is an organisation membership, setup the context etarcted from the order response
                            this.context.selectedSchool = {
                                constituentId: membershipDetails.Recipient.Id,
                                membershipDetails: {
                                    Id: membershipDetails.Id,
                                    Constituent: {
                                        DisplayName: membershipDetails.Recipient.DisplayName,
                                        SortName: membershipDetails.Recipient.SortName,
                                    }
                                },
                                // Set total available greater than 0 to ensure the user can be made an admin without needing
                                // offering admin status step as a purchase membership would indicate no admins assigned yet
                                totalAvailable: 1,
                            }
                            this.context.constituentRecord = orders.details.Constituent
                            this.context.constituentId = orders.details.Constituent.Id
                            this.paymentProcessingStatus.inProgress = false;
                            this.setPaymentStatus(paymentStatus);
                            await this.addUserToSchool(this.context.constituentId, this.context.selectedSchool.constituentId, this.context.selectedSchool.membershipDetails, this.context.selectedSchool.totalAvailable);
                            await this.makeUserAdmin()
                            this.paymentComplete()
                            return
                        }
                    }
                } else {
                    this.setPaymentStatus(paymentStatus);
                }

            } else {
                throw new Error('Invalid payment status received.');
            }

            console.log(this.context.sessionData)
        } catch (error) {
            console.error("Payment process failed:", error);
            this.error = "Payment processing failed. Please try again.";
            this.paymentProcessingStatus.inProgress = false;
            this.setPaymentStatus('failed');
        } finally {
            console.log('I am called in handlePaymentProcess')
            this.loading = false;
        }
    },
    async fetchCountries() {

        return await API.GET.countries()
            .then(response => {
                if (!response.ok) throw new Error('Failed to fetch countries');
                return response.json();
            })
            .then(data => {
                return data?.countries.map(country => ({
                    label: country.Description,
                    value: String(country.Id),
                }));
            });
    },
    async fetchStates() {

        return await API.GET.states()
            .then(response => {
                if (!response.ok) throw new Error('Failed to fetch states');
                return response.json();
            })
            .then(data => {
                return data?.states.map(state => ({
                    countryValue: String(state.Country.Id),
                    label: state.Description,
                    value: String(state.Id),
                }));
            });
    },
    setupWatchers() {
        this.$watch('context.pathway', (value) => {
            this.context.pathwaySteps = this.pathways[value]?.steps || [];
            if (this.pathways[value]?.requires.schoolSearch) {
                this.$nextTick(() => {
                    if (!this.autocompleteInstance) {
                        this.initSchoolAutocomplete();
                    }
                });
            }
        });

        this.$watch('manualSchoolInput', (value) => {
            if (this.context.pathway !== 'individual' && value.schoolName && value.addressLine1 && value.postalCode) {
                this.context.selectedSchool = {...value};
                console.log(this.context.selectedSchool);
            } else if (value.addressLine1 && value.postalCode) {
                this.context.selectedSchool = {
                    ...value
                };
            } else {
                this.context.selectedSchool = null;
            }
        });

        this.$watch('manualSchoolInput.country', (country) => {
            this.filteredStates = !country
                ? this.allStates
                : this.allStates.filter(state => state.countryValue === country);

            const hasStates = this.filteredStates.length > 0;

            // Toggle required flag used by your template's :required binding
            this.schoolForm.state.required = hasStates;

            // If no states for this country, ensure state is null (not empty string)
            if (!hasStates) {
                this.manualSchoolInput.state = null;
            } else if (this.manualSchoolInput.state == null) {
                // If states now exist and we previously nulled it, keep empty so user chooses
                this.manualSchoolInput.state = '';
            }
        });

        this.$watch('manualSchoolInput.state', (stateValue) => {
            const selectedState = this.allStates.find(s => s.value === stateValue);
            if (selectedState && !this.manualSchoolInput.country) {
                this.manualSchoolInput.country = selectedState.countryValue;
            }
        });
    },
    setChosenPlan(plan) {
        this.selectedPlan = this.subscriptionPlans[plan];
        this.selectedPlan.numberOfUsers = this.numberOfUsers;
        this.selectedPlan.type = plan;
        const step = this.context.pathway === 'individual' ? 'confirmSubscriptionIndividual' : 'confirmSubscriptionOther';

        const result = registrationMachine.transition(this.state, 'NEXT_STEP', {
            pathwaySteps: this.context.pathwaySteps,
            forceStep: step,
        })
        this.state = result.nextState;
    },

    resetTypeSelector() {
        this.context.pathway = null
        this.context.selectedSchool = null
        for (const key in this.manualSchoolInput) {
            this.manualSchoolInput[key] = '';
        }

        if (this.autocompleteInstance) {
            this.autocompleteInstance.setQuery('')
        }
    },
    show(step) {
        return this.state === step;
    },

    choosePathway(pathwayKey) {
        if (!pathwayKey || !this.pathways[pathwayKey]) {
            this.error = 'Please select a valid school type.';
            return;
        }

        const result = registrationMachine.transition(this.state, 'PATHWAY_CHOSEN', {pathway: pathwayKey});

        this.context.pathway = result.updates.pathway;
        this.context.pathwaySteps = result.updates.pathwaySteps;
        this.state = result.nextState;
        if (this.state === 'findSchool') {
            this.$nextTick(() => {
                if (!this.autocompleteInstance) {
                    this.initSchoolAutocomplete();
                }
            });
        }
    },
    next(forceStep) {

        if (!this.validateForm()) return;

        if (!this.context.pathwaySteps || !this.context.pathwaySteps.length) {
            return;
        }
        console.log(this.context.pathwaySteps)

        const result = registrationMachine.transition(this.state, 'NEXT_STEP', {
            pathwaySteps: this.context.pathwaySteps,
            forceStep: forceStep || null
        });
        this.state = result.nextState
    },
    paymentComplete() {
        const result = registrationMachine.transition(this.state, 'PAYMENT_COMPLETE', {});
        this.state = result.nextState;
    },
    back(forceStep) {
        const result = registrationMachine.transition(this.state, 'BACK', {
            pathwaySteps: this.context.pathwaySteps,
            forceStep: forceStep || null
        });
        this.state = result.nextState;
    },
    close() {
        window.location.href = "/"
    },
    async accountLookUp() {
        if (!this.validateForm()) return;
        if (!this.context.email) {
            this.errors.email = 'Please enter a valid email address';
            return;
        }

        const email = this.context.email.toLowerCase().trim();

        if (!email) {
            this.errors.email = "Please enter your email.";
            return;
        }

        try {
            this.loading = true;
            const response = await API.GET.accountLookup(email);
            if (!response.ok) {
                this.error = 'Account lookup failed. Please try again.';
                return;
            }

            const data = await response.json();


            if (data.resetPassword) {
                // Allow registration to continue
                const result = registrationMachine.transition(this.state, 'NEXT_STEP', {
                    pathwaySteps: this.context.pathwaySteps
                });
                this.state = result.nextState;
                this.error = '';
            } else if (data.email === email) {
                // Existing account found
                this.existingAccount = true;
                this.error = '';
            } else {
                // Allow to continue to registration anyway
                const result = registrationMachine.transition(this.state, 'NEXT_STEP', {
                    pathwaySteps: this.context.pathwaySteps
                });
                this.state = result.nextState;
                this.error = 'No account found with that email address. Please register.';
            }

        } catch (err) {
            this.error = 'An error occurred during lookup. Please try again.';
            console.error('Account lookup error:', err);
        } finally {
            this.loading = false;
        }
    },
    async organisationSearchByPostCode(postalCode) {
        const query = {
            type: "basic",
            op: "Like",
            post: postalCode
        }
        try {
            this.loading = true;
            const response = await API.GET.constituentSearch(query);
            if (!response.ok) {
                throw new Error('Failed to search for school by postcode');
            }
            const data = await response.json();
            if (data.success) {
                return data.Id
            }

        } catch (error) {
            this.error = "Could not search for school by postcode. Please try again.";
            console.error(error);
        } finally {
            this.loading = false;
        }
        return 0;
    },
    async confirmSchoolLink() {
        console.log(this.context.selectedSchool)
        const pathway = this.context.pathway;
        console.log(pathway)
        const requires = this.pathways[pathway]?.requires || {};

        if (requires.unableToCompleteOnline) {
            const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                pathwaySteps: this.context.pathwaySteps,
                forceStep: "contactSupport",
            });
            this.state = result.nextState;
            return;
        }

        const stateId = this.resolveStateId(this.context.selectedSchool);

        if (pathway === 'individual') {
            // fetch individual record by constituentId
            const userResponse = await API.GET.constituent(this.context.constituentId);
            if (!userResponse.ok) {
                this.error = "Failed to fetch user details. Please try again.";
                console.error('Error fetching user details:', userResponse.statusText);
                return;
            }
            const userData = await userResponse.json();
            console.log(userData);
            this.context.constituentRecord = userData?.details;
            // update the address details for individual home school user
            const existingAddress = userData?.details?.Addresses?.find(addr => addr.PrimaryIndicator && addr.AddressType.Id === settings.IndividualAddressTypeId) || null;
            const addressPayload = {
                AddressType: {Id: settings.IndividualAddressTypeId},
                Constituent: {Id: this.context.constituentId},
                Label: true,
                Months: "YYYYYYYYYYYY",
                PrimaryIndicator: true,
                Street1: this.context.selectedSchool.addressLine1,
                Street2: this.context.selectedSchool?.addressLine2 || "",
                PostalCode: this.context.selectedSchool.postalCode,
                City: this.context.selectedSchool.city,
                Country: {Id: parseInt(this.context.selectedSchool.country)},
                State: this.stateObjectOrNull(stateId),
                Inactive: false,

            }
            if (existingAddress) {
                console.log("User had existing address, updating it")
                addressPayload["Id"] = existingAddress.Id;
                addressPayload["UpdatedDateTime"] = existingAddress.UpdatedDateTime;
                await API.PUT.updateAddress(this.csrfToken, existingAddress.Id, addressPayload)
            } else {
                console.log(addressPayload)
                console.log("User had no existing address, creating it")
                await API.POST.createAddress(this.csrfToken, addressPayload)
            }
        } else {
            let organisationConstituentId = this.context.selectedSchool.constituentId || false;

            // Check if we have don't have constituentId for the selected school via algolia, check via postcode in Tessitura
            if (!organisationConstituentId) {
                if (this.context.selectedSchool.postalCode) {
                    organisationConstituentId = await this.organisationSearchByPostCode(this.context.selectedSchool.postalCode.trim());
                }
            }
            // If we still don't have a constituentId then add it to Tessitura organisations
            if (!organisationConstituentId) {
                console.log("Creating a school in Tessitura")
                // Create a new organisation in Tessitura and get the constituentId
                const payload = {
                    ConstituentType: {Id: settings.OrganizationConstituentTypeId},
                    OriginalSource: {Id: settings.OrganizationOriginalSourceId},
                    LastName: this.context.selectedSchool?.schoolName || this.context.selectedSchool?.name,
                }

                const address = {
                    AddressType: {Id: settings.OrganizationAddressTypeId},
                    Label: true,
                    Months: "YYYYYYYYYYYY",
                    PrimaryIndicator: true,
                    Street1: this.context.selectedSchool.addressLine1,
                    Street2: this.context.selectedSchool?.addressLine2 || "",
                    City: this.context.selectedSchool.city,
                    postalCode: this.context.selectedSchool.postalCode,
                    Country: {Id: parseInt(this.context.selectedSchool.country) || 209},
                    State: this.stateObjectOrNull(stateId),
                };

                // const phoneNumber = {
                //     PrimaryIndicator: true,
                //     PhoneNumber: this.context.selectedSchool.phone,
                //     PhoneType: {Id: settings.OrganizationPhoneTypeId},
                // };
                payload.Addresses = [address];
                payload.ElectronicAddresses = [];


                payload.ElectronicAddresses.push({
                    Address: this.context.email,
                    ElectronicAddressType: {Id: 1},
                    AllowHtmlFormat: true,
                    Months: "YYYYYYYYYYYY",
                    PrimaryIndicator: true,
                    IsEmail: true,
                });


                const response = await API.POST.constituent(this.csrfToken, payload);
                if (!response.ok) {
                    this.error = "Failed to create school in Tessitura. Please try again.";
                    console.error('Error creating school:', response.statusText);
                    return;
                }
                const data = await response.json();

                organisationConstituentId = data.data.Id
            }
            // check if the school with the constituentId has a subscription
            this.context.selectedSchool.constituentId = organisationConstituentId;
            console.log("Organisation Id", organisationConstituentId)
        }

        if (requires.domainValidation) {
            if (!this.validateForm()) {
                return;
            }

            const attributesResponse = await API.GET.fetchAttributes(this.context.selectedSchool.constituentId, {
                keywordIds: settings.EmailDomainAttributeId
            });
            if (!attributesResponse?.ok) {
                this.error = "Failed to get attributes.";
                console.error('Failed to fetch the attributes:', attributesResponse?.statusText);
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: "invalidEmail",
                });
                this.state = result.nextState;
                return;
            }

            const attributesResponseData = await attributesResponse.json()
            const attributes = attributesResponseData?.details;
            if (!Array.isArray(attributes) || attributes.length === 0) {
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: "invalidEmail",
                });
                this.state = result.nextState;
                return;
            }

            // Build Allowlist
            const emailDomains = attributes
                .filter(a => a?.Keyword?.Id === settings.EmailDomainAttributeId && typeof a?.Value === "string")
                .map(a => a.Value.trim().toLowerCase())
                .filter(Boolean);

            // Quick exit if no domains configured
            if (emailDomains.length === 0) {
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: "invalidEmail",
                });
                this.state = result.nextState;
                return;
            }

            const allowed = new Set(emailDomains);

            // Extract & normalize user domain
            const currentEmailDomain = this.context.email?.split("@")[1]?.trim().toLowerCase() || "";

            if (!currentEmailDomain) {
                console.log("No valid email");
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: "invalidEmail",
                });
                this.state = result.nextState;
                return;
            }

            let isAllowed = allowed.has(currentEmailDomain);

            if (!isAllowed) {
                console.log("Email domain check failed");
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: "invalidEmail",
                });
                this.state = result.nextState;
                return;
            }
        }

        let membership = false;
        if (pathway === 'individual') {
            membership = await this.checkIndividualMembershipExists()
        } else {
            membership = await this.checkMembershipExists()
        }


        if (!membership) {
            if (requires.paidSubscription) {
                const noActiveSubscriptionStep = pathway === 'individual' ? 'noActiveSubscriptionIndividual' : 'noActiveSubscriptionOther'

                // No subscription in place for the school - write function to check the subscription status of the school
                const result = registrationMachine.transition(this.state, "NEXT_STEP", {
                    pathwaySteps: this.context.pathwaySteps,
                    forceStep: noActiveSubscriptionStep,
                });
                this.state = result.nextState;
                return;
            }
        }

        console.log("Membership exists", membership)
        console.log(this.context.selectedSchool.membershipDetails)

        if (this.offerAdmin) {
            this.next("offerAdminStatus");
        } else if (['independent-school', 'international'].includes(pathway)) {
            const schoolAffiliations = await API.GET.affiliations({
                groupConstituentID: this.context.selectedSchool.constituentId,
                currentOnly: true,
            });
            if (!schoolAffiliations.ok) {
                this.error = "Failed to fetch school affiliations. Please try again.";
                console.error('Error fetching school affiliations:', schoolAffiliations.statusText);
                return;
            }

            const affiliationsData = await schoolAffiliations.json();
            const affiliations = affiliationsData.details
            const hasAdmins = Array.isArray(affiliations) && affiliations.some(aff => aff?.AffiliationType?.Id === settings.AdminAffiliationTypeId);
            console.log(hasAdmins)
            if (!hasAdmins) {
                const user = await API.GET.constituent(this.context.constituentId);
                if (!user.ok) {
                    this.error = "Failed to fetch user details. Please try again.";
                    console.error('Error fetching user details:', user.statusText);
                    return;
                }
                const constituent = await user.json();

                this.context.constituentRecord = constituent.details;
                console.log(this.context.constituentRecord);
                this.next("offerAdminStatus");
                return;
            }
            this.next("contactYourAdmin")
        } else {
            this.next("done");
        }
    },
    normalizeStr(s) {
        return (s || '').toString().trim().toLowerCase();
    },

    resolveStateId(school) {
        if (!school) return null;

        // 1) Explicit state value (from select or Algolia record)
        if (school.state) {
            const parsed = parseInt(school.state, 10);
            if (!Number.isNaN(parsed)) return parsed;
        }

        // 2) Map from county label -> state option for the same country (if possible)
        if (school.county) {
            const countyNorm = this.normalizeStr(school.county);
            const countryVal = school.country ? String(school.country) : null;

            // If we know the country, filter states by that country for better accuracy
            const pool = countryVal
                ? this.allStates.filter(s => s.countryValue === countryVal)
                : this.allStates;

            const match = pool.find(s => this.normalizeStr(s.label) === countyNorm);
            if (match) {
                const parsed = parseInt(match.value, 10);
                if (!Number.isNaN(parsed)) return parsed;
            }
        }

        // 3) Nothing found → return null (required attribute will be sent as null)
        return null;
    },
    stateObjectOrNull(id) {
        return id ? {Id: id} : null;
    },
    async checkIndividualMembershipExists() {
        const response = await API.GET.membershipSummary(this.context.constituentId);
        if (!response.ok) {
            this.error = "Failed to fetch membership summary. Please try again.";
            console.error('Error fetching membership summary:', response.statusText);
            return;
        }
        const data = await response.json();
        const membershipDetails = data?.details || null;
        if (!membershipDetails) {
            // No membership found for the individual, so we need to create one
            console.log("No membership found for the individual, needs to purchase one");
            this.error = "No membership found for the individual, needs to purchase one";
            return false;
        }
        this.context.selectedSchool.membershipDetails = membershipDetails;
        const membershipExpiryDate = new Date(membershipDetails.ExpirationDateTime);
        const currentDate = new Date();

        if (membershipExpiryDate < currentDate) {
            // Membership has expired, so we need to renew
            console.log("Membership has expired, needs to be renewed");
            this.error = "Membership has expired, needs to be renewed";
            return false;
        }
        console.log("Membership details", membershipDetails);
        return true
    },
    async checkMembershipExists() {
        const organisationConstituentId = this.context.selectedSchool.constituentId
        const response = await API.GET.membershipSummary(this.context.selectedSchool.constituentId);
        if (!response.ok) {
            this.error = "Failed to fetch membership summary. Please try again.";
            console.error('Error fetching membership summary:', response.statusText);
            return;
        }
        const data = await response.json();
        let membershipDetails = data?.details || null;
        if (!membershipDetails) {
            // No membership found for the school, so we need to create one
            console.log("No membership found for the school, determine type to determine membership");

            const type = this.context.selectedSchool.type
            if (type === 'state') {
                // add the State Membership
                const response = await API.POST.membershipCreate(this.csrfToken, this.context.selectedSchool.constituentId, {
                    type,
                })
                if (!response.ok) {
                    this.error = "Failed to create membership for the school. Please try again.";
                    console.error('Error creating membership:', response.statusText);
                    return;
                }
                const data = await response.json();
                membershipDetails = data.details;
            }
        }

        if (!membershipDetails) {
            return false
        }
        this.context.selectedSchool.membershipDetails = membershipDetails;
        // check if membership is still valid
        const membershipExpiryDate = new Date(membershipDetails.ExpirationDateTime);
        const currentDate = new Date();

        if (membershipExpiryDate < currentDate) {
            // Membership has expired, so we need to renew
            console.log("Membership has expired, needs to be renewed");
            this.error = "Membership has expired, needs to be renewed";
            return false;
        }

        if (this.context.pathway === 'state-school') {
            return await this.applyMembershipDetailsToSchoolAndUser(membershipDetails, organisationConstituentId)
        }

        return true
    },
    async applyMembershipDetailsToSchoolAndUser(membershipDetails, organisationConstituentId) {
        console.log("Membership details", membershipDetails);
        // Check membership expiry date and if it is still valid
        const membershipExpiryDate = new Date(membershipDetails.ExpirationDateTime);
        const currentDate = new Date();
        if (membershipExpiryDate < currentDate) {
            // Membership has expired, so we need to renew
            console.log("Membership has expired, needs to be renewed");
            this.error = "Membership has expired, needs to be renewed";
            return;
        }
        // Check the number of users in the membership and if it is sufficient for the school
        const benefits = await API.GET.checkNumberOfSeatsForMembership(organisationConstituentId);
        if (!benefits.ok) {
            this.error = "Failed to check number of seats for membership. Please try again.";
            console.error('Error checking number of seats:', benefits.statusText);
            return;
        }
        const benefitsData = await benefits.json();
        const benefitsDetails = benefitsData.details;

        if (benefitsDetails) {
            const benefitForMembership = benefitsDetails.find(b => b.BenefitMembershipSummary.Id === membershipDetails.Id);
            console.log("benefitForMembership", benefitForMembership);
            if (!benefitForMembership) {
                // No seats available for the membership, so we need to renew
                console.log("No seats available for the membership, needs to be renewed");
                this.error = "No seats available for the membership, needs to be renewed";
                return;
            }
            console.log(benefitForMembership)

            const unlimitedSeats = benefitForMembership.MembershipLevelBenefit.UnlimitedIndicator;
            const totalAvailable = Number(benefitForMembership.TotalAvailable);
            const remainingSeats = Number(benefitForMembership.TotalRemaining);
            const schoolAffiliations = await API.GET.affiliations({
                groupConstituentID: organisationConstituentId,
                currentOnly: true,
            });
            if (!schoolAffiliations.ok) {
                this.error = "Failed to fetch school affiliations. Please try again.";
                console.error('Error fetching school affiliations:', schoolAffiliations.statusText);
                return;
            }

            const affiliationsData = await schoolAffiliations.json();
            const affiliations = affiliationsData.details

            this.context.selectedSchool.totalAvailable = totalAvailable;

            if (unlimitedSeats) {
                // This is a state school membership with unlimited seats
                console.log("Unlimited seats available for the membership");
                const hasAdmins = Array.isArray(affiliations) && affiliations.some(aff => aff?.AffiliationType?.Id === settings.AdminAffiliationTypeId);

                if (!hasAdmins) {
                    // No users in the school, so we can offer this user to be an admin later in the process
                    console.log("No users or admins in the school add user");
                    await this.addUserToSchool(this.context.constituentId, this.context.selectedSchool.constituentId, membershipDetails, totalAvailable);

                    // Send the user to the admin status offer screen
                    console.log("Send the user to the admin status offer screen");
                    this.offerAdmin = true;
                } else {
                    // There are users in the school, so we cannot make this user an admin
                    console.log("There are users in the school so add user and not offer to make admin");
                    await this.addUserToSchool(this.context.constituentId, this.context.selectedSchool.constituentId, membershipDetails, totalAvailable);
                }
            } else {
                if (remainingSeats > 0) {
                    // There are remaining seats available for the membership
                    console.log("Remaining seats available for the membership");
                    const hasAdmins = Array.isArray(affiliations) && affiliations.some(aff => aff?.AffiliationType?.Id === settings.AdminAffiliationTypeId);
                    if (!hasAdmins) {
                        // No users in the school, so we can offer this user to be an admin later in the process
                        console.log("No users in the school add user");
                        await this.addUserToSchool(this.context.constituentId, this.context.selectedSchool.constituentId, membershipDetails, totalAvailable);
                        // Send the user to the admin status offer screen
                        this.offerAdmin = true;

                    } else {
                        // There are users in the school, so we cannot make this user an admin
                        console.log("There are admin in the school so add user and not offer to make admin");
                        await this.addUserToSchool(this.context.constituentId, this.context.selectedSchool.constituentId, membershipDetails, totalAvailable);
                    }
                }
            }

            return true
        }

        return false

    },
    async addUserToSchool(constituentId, organisationConstituentId, membershipDetails, totalAvailable) {
        console.log("addUserToSchool - Membership details", membershipDetails);
        const user = await API.GET.constituent(this.context.constituentId);
        if (!user.ok) {
            this.error = "Failed to fetch user details. Please try again.";
            console.error('Error fetching user details:', user.statusText);
            return;
        }
        const constituent = await user.json();

        this.context.constituentRecord = constituent.details;

        // Add benefit usage record for user
        const benefitUsageResponse = await API.POST.createManualBenefitUsage(this.csrfToken, {
            Benefit: {
                Id: settings.UserSeatMembershipOrganizationBenefitId,
                Inactive: false,
                MembershipOrganization: {
                    Id: settings.MembershipOrganizationId,
                },
                Type: {
                    Id: -2,
                },
                PremiumId: null
            },
            Constituent: {
                Id: organisationConstituentId,
            },
            Membership: {
                Id: membershipDetails.Id,
            },
            TotalCount: 1,
            UsedDateTime: new Date().toISOString(),
        })

        if (!benefitUsageResponse.ok) {
            this.error = "Failed to create benefit usage record. Please try again.";
            console.error('Error creating benefit usage record:', benefitUsageResponse.statusText);
            return;
        }

        const benefitUsageData = await benefitUsageResponse.json();

        this.context.selectedSchool.benefitUsage = benefitUsageData

        const basePayload = {
            GroupConstituent: {Id: organisationConstituentId},
            IndividualConstituent: {Id: this.context.constituentId},
            IsIncludedInSearchResults: true,
            IndividualConstituentName: this.context.constituentRecord.DisplayName,
            GroupConstituentName: membershipDetails.Constituent.DisplayName,
            IndividualConstituentSortName: this.context.constituentRecord.SortName,
            GroupConstituentSortName: membershipDetails.Constituent.SortName,
            StartDate: new Date().toISOString(),
        }


        // Add affiliation for the user as a teacher to the school
        const responseAddedForBeingATeacher = await API.POST.affiliation(this.csrfToken, this.context.constituentId, {
            AffiliationType: {Id: settings.TeacherAffiliationTypeId},
            ...basePayload,
        })

        const userPayload = {
            AffiliationType: {Id: settings.UserAffiliationTypeId},
            ...basePayload,
            Salary: benefitUsageData.details.Id,
        }

        if (totalAvailable && totalAvailable > 0) {
            userPayload.EndDate = membershipDetails.ExpirationDateTime;
        }

        // Add affiliation for user for access to the platform
        const response = await API.POST.affiliation(this.csrfToken, this.context.constituentId, userPayload)

        return response.json()
    },
    async makeUserAdmin() {

        const benefitUsageResponse = await API.POST.createManualBenefitUsage(this.csrfToken, {
            Benefit: {
                Id: settings.AdminSeatMembershipOrganizationBenefitId,
                Inactive: false,
                MembershipOrganization: {
                    Id: settings.MembershipOrganizationId,
                },
                Type: {
                    Id: -2,
                },
                PremiumId: null
            },
            Constituent: {
                Id: this.context.selectedSchool.constituentId,
            },
            Membership: {
                Id: this.context.selectedSchool.membershipDetails.Id,
            },
            TotalCount: 1,
            UsedDateTime: new Date().toISOString(),
        })

        const benefitUsageData = await benefitUsageResponse.json();

        const adminPayload = {
            AffiliationType: {Id: settings.AdminAffiliationTypeId}, // Admin type
            GroupConstituent: {Id: this.context.selectedSchool.constituentId},
            GroupConstituentName: this.context.selectedSchool.membershipDetails.Constituent.DisplayName,
            GroupConstituentSortName: this.context.selectedSchool.membershipDetails.Constituent.SortName,
            IndividualConstituent: {Id: this.context.constituentId},
            IndividualConstituentSortName: this.context.constituentRecord.SortName,
            IsIncludedInSearchResults: true,
            StartDate: new Date().toISOString(),
            Salary: benefitUsageData.details.Id,
        }

        if (this.context.selectedSchool?.totalAvailable && this.context.selectedSchool.totalAvailable > 0) {
            // Add the end date to the admin affiliation if the membership has a limit on the number of users
            adminPayload.EndDate = this.context.selectedSchool.membershipDetails.ExpirationDateTime;
        }

        // Add the user to the school as an admin
        const response = await API.POST.affiliation(this.csrfToken, this.context.constituentId, adminPayload);

        if (!response.ok) {
            this.error = "Failed to make user an admin. Please try again.";
            console.error('Error making user an admin:', response.statusText);
            return;
        }

        this.next('done');
    },
    async confirmSubscription() {
        // Send this to Windcave to take payment and then response from Windcave and update of Tess API will confirm if the subsciption is valid
        console.log("Selected plan", this.selectedPlan);
        let membershipLevelId = null
        const sortedLevels = Object.entries(this.memberLevelSeatNumbers)
            .sort((a, b) => a[1] - b[1]);
        for (const [levelId, seats] of sortedLevels) {
            if (this.selectedPlan.numberOfUsers <= seats) {
                membershipLevelId = levelId;
                break;
            }
        }

        if (!membershipLevelId) {
            this.error = "Selected number of users exceeds the maximum allowed for the selected plan.";
            return;
        }
        console.log("Membership Level Id", membershipLevelId);
        try {
            this.loading = true;
            const payload = {
                FundId: settings.FundId,
                MembershipLevelId: parseInt(membershipLevelId, 10),
                Action: 1,
                ConstituentId: this.context.constituentId,
                OrganisationConstituentId: this.context.selectedSchool?.constituentId || false,
                PlanType: this.selectedPlan.type,
                NumberOfUsers: parseInt(this.numberOfUsers),
            }
            // add the membership to the basket and the once receieve response that has been added to the basket, send the request to Windcave
            const orderResponse = await API.POST.addToBasket(this.csrfToken, payload);
            if (!orderResponse.ok) {
                this.error = "Failed to add subscription to basket. Please try again.";
                console.error('Error adding subscription to basket:', response.statusText);
                return;
            }
            const orderDetails = await orderResponse.json();
            if (orderDetails.success) {
                console.log(orderDetails);
                // Send redirect to windcave to take payment
                if (orderDetails?.payment) {
                    let paymentUrl = null
                    for (const link of orderDetails?.payment?.links) {
                        if (link.method === "REDIRECT") {
                            paymentUrl = link?.href;
                        }
                    }
                    if (paymentUrl) {
                        window.location.href = paymentUrl;
                    }
                }
            }
        } catch (error) {
            this.loading = false;
            this.error = "An error occurred while processing your subscription. Please try again.";
            console.error('Error during subscription confirmation:', error);
        } finally {
            this.loading = false;
        }

    },
    async createAccount() {
        if (!this.validateForm()) return;
        try {
            const res = await API.POST.registerAccount(this.csrfToken, {
                first_name: this.firstname,
                last_name: this.lastname,
                phone_number: this.phoneNumber,
                email: this.context.email,
                password: this.password,
                next: this.nextTo,
            });
            const data = await res.json();
            if (data.IsLoggedIn) {
                window.location.href = this.nextTo;
            } else {
                this.error = 'Registration failed.';
            }
        } catch (e) {
            this.error = 'An error occurred during account creation.';
            console.error(e);
        }
    },
    validateForm() {
        const visibleSections = Array.from(this.$root.querySelectorAll('[x-ref]'))
            .filter(ref => {
                const el = this.$refs[ref.getAttribute('x-ref')];
                return el && el.offsetParent !== null;
            });

        for (const section of visibleSections) {
            const fields = section.querySelectorAll('input, select, textarea');
            for (const field of fields) {
                const name = field.name || field.id || 'field';
                if (field.checkValidity && !field.checkValidity()) {
                    this.errors[name] = field.validationMessage;
                    field.focus();
                    field.reportValidity?.();
                    return false;
                } else {
                    delete this.errors[name];
                }
            }
        }
        return true;
    },
    passwordHint: passwordHint,
    initSchoolAutocomplete() {
        if (this.autocompleteInstance) return;

        this.autocompleteInstance = autocomplete({
            container: this.$refs.autocompleteContainer,
            panelContainer: this.$refs.autocompleteContainer,
            placeholder: 'e.g. My School Name or 123456',
            detachedMediaQuery: "none",
            getSources: () => [{
                sourceId: 'schools',
                getItems: async ({query}) => {
                    if (!query) return [];
                    const {hits} = await this.searchClient.searchSingleIndex({
                        indexName: algoliaIndexName,
                        searchParams: {
                            query,
                            highlightPreTag: '<span class="c-algolia-highlight">',
                            highlightPostTag: '</span>',
                        },
                    });
                    return hits;
                },
                templates: {
                    item({item, components, html}) {
                        const highlight = item._highlightResult;
                        const name = highlight.name?.value || '';
                        const urn = highlight.URN?.value ? ` <span>(${highlight.URN.value})</span>` : '';
                        const postalCode = highlight.postalCode?.value || '';
                        const address = [
                            item.addressLine1,
                            item.addressLine2,
                            item.addressLine3,
                            item.city,
                        ].filter(item => item).join(', ');
                        return html`
                            <div class="aa-ItemContent">
                                <div class="aa-ItemTitle">
                                    ${html([name])}${html([urn])}
                                </div>
                                <div class="aa-ItemSubtitle">
                                    ${address}, ${html([postalCode])}
                                </div>
                            </div>
                        `;
                    },
                },
                onSelect: ({item, setQuery, setIsOpen}) => {
                    setQuery(item.name);
                    setIsOpen(false);
                    this.context.selectedSchool = item;
                    this.context.postcode = item.postalCode || '';
                },
            }],
        });
    }
});